import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { members as membersApi, families as familiesApi, memberList, batch, memberRegistrations } from '../../api/endpoints';
import type { BatchOperation, RegistrationPerson } from '../../api/endpoints';
import type { DuplicateItem } from '../../api/endpoints';
import api, { getList, getTotal } from '../../api/client';
import {
  useFetchList, useNotification, useOptionList, useChurchConfig
} from '../../hooks';
import { getPreferences } from '../../utils/preferences';
import { formatDate, ageFrom, todayISO } from '../../utils/format';
import { isVoided } from '../../utils/records';
// import { useReadOnly } from '../../utils/readOnly'; // unused while the photo UI is commented out
// import { avatarUrl } from '../../utils/avatar'; // unused while the photo UI is commented out
import useStyles from '../../theme/useStyles';
import {
  PageHeader, SearchBar, SubTabs, Pagination, Notification, FormLayout, Input, Select, SearchableSelect, FileInput, Drawer, Button, Badge, Modal, ConfirmDialog, PromptDialog, RowActions, TablePanel, PrintButton
} from '../../components/ui';
import validateMember, { hasMarriageDate } from './validateMember';
import {
  isGuest, guestPhase, guestPhaseLabel, daysUntil, addMonths,
  GUEST_STATUS, ROLL_STATUS, DEFAULT_GUEST_MONTHS
} from './guest';
import type { GuestPhase } from './guest';
import ChangeStatusDialog from './ChangeStatusDialog';
import { LIFE_OPTIONS, lifeLabel, lifeOf } from './lifeStatus';
import { getStoredUser, isAdmin } from '../../utils/auth';
import type { Member, Family } from '../../types';

const GENDERS = ['Male', 'Female'];
const RELATIONS = ['Head', 'Spouse', 'Son', 'Daughter', 'Father', 'Mother', 'Other'];
/**
 * What a member joining a family can be. A family has one head: a new head is
 * made with "Add Head", or an existing member is promoted from their record.
 */
const MEMBER_RELATIONS = RELATIONS.filter((r) => r !== 'Head');
const MARITAL_STATUSES = ['Single', 'Married', 'Widowed', 'Divorced'];
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const COMMUNION_STATUSES = ['Received', 'Not Received', 'In Preparation'];
/** Lifecycle status — drives the tabs and the head-demotion rule. Fixed set. */

/** Free-form membership categories, extendable by users (e.g. "NRI"). */

const DOCUMENTS = [
  { key: 'baptismCert', label: 'Baptism Certificate' },
  { key: 'marriageCert', label: 'Marriage Certificate' },
  { key: 'membershipCert', label: 'Membership Certificate' },
  { key: 'idProof', label: 'ID Proof (Aadhaar/PAN)' }
];

/** Editable member fields; `memberId` appears only once a record is loaded. */
type MemberForm = Omit<Partial<Member>, 'gender'> & { gender?: Member['gender'] | '' };

const EMPTY_FORM: MemberForm = {
  familyId: '', memberStatus: '', barcodeMembershipNo: '',
  name: '', relativeName: '', gender: '', location: '', dateOfBirth: '',
  relationToHead: '', maritalStatus: '', dateOfMarriage: '', bloodGroup: '',
  education: '', occupation: '', mobileNumber: '', aadhaarNumber: '',
  photo: '', photoDataUrl: '',
  status: 'Alive', dateOfDeath: '', transferChurch: '',
  guestFrom: '', guestUntil: '', guestOrigin: '', guestPurpose: '', membershipDate: '',
  baptismDate: '', confirmationDate: '', communionStatus: '',
  baptismCert: '', baptismCertUrl: '', marriageCert: '', marriageCertUrl: '',
  membershipCert: '', membershipCertUrl: '', idProof: '', idProofUrl: ''
};

/** Certificates are stored as data URLs; PDFs need an iframe, images an <img>. */
const isPdf = (doc) =>
  String(doc?.dataUrl || '').startsWith('data:application/pdf')
  || String(doc?.fileName || '').toLowerCase().endsWith('.pdf');

/**
 * Keeps a stored value selectable even when it predates the current option list
 * (e.g. records saved while Communion Status was Yes/No). Without this the field
 * renders blank and the value is lost on the next save.
 */
const withStoredValue = (options: string[], value?: string): string[] =>
  value && !options.includes(value) ? [...options, value] : options;

const isHead = (relation) => String(relation || '').toLowerCase() === 'head';
const familyIdOf = (f) => f.familyId || `FAM-${f.id}`;
const statusOf = (m) => String(m.status || 'Alive').toLowerCase();

/** The list tab a saved record belongs on. Guests have their own tab. */
const viewFor = (m) => {
  const life = statusOf(m);
  if (life === 'deceased' || life === 'transferred' || life === 'inactive') return life;
  return isGuest(m) ? 'guest' : 'directory';
};

/** Badge tone for where a guest stay stands. */
const GUEST_TONE: Record<GuestPhase, 'success' | 'warning' | 'error' | 'neutral'> = {
  active: 'success', expiring: 'warning', expired: 'error', open: 'neutral'
};

/** The Guests tab's sub-filters, each a backend bucket. */
const GUEST_FILTERS = [
  { key: 'guest', label: 'All guests', countKey: 'guests' },
  { key: 'guest-expiring', label: 'Ending within 30 days', countKey: 'guestsExpiring' },
  { key: 'guest-expired', label: 'Stay ended', countKey: 'guestsExpired' }
] as const;
type GuestFilter = (typeof GUEST_FILTERS)[number]['key'];

/**
 * Tab keys the screen owns. A Member Status tab is derived from user-entered
 * data, so a category typed as "void" would otherwise hijack the delete tab.
 */
const RESERVED_TABS = ['directory', 'inactive', 'deceased', 'transferred', 'void', 'registration', 'guest'];

/**
 * Members and families arrive tagged so one fetch can carry both. The tag must
 * never be written back: `MemberService.update()` is a full replace, so the
 * payload has to be the record and nothing else.
 */
const stripKind = (row) => {
  const { __kind, ...rest } = row || {};
  return rest;
};

/** How `deletedAt` is stored — a display string, matching Families. */
const stamp = () =>
  new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

/** Filename for the browser's Save-as-PDF dialog. */
const pdfNameFor = (m) =>
  `Member-${m.memberId || m.id}-${String(m.name || 'record').replace(/[^\w-]+/g, '-')}`;

/**

/** Debounce a value — used to hold the search term steady between keystrokes. */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** Map a backend duplicate message to the form field it belongs on. */
const dupFieldFor = (message?: string): string | null => {
  const m = String(message || '').toLowerCase();
  if (m.includes('aadhaar')) return 'aadhaarNumber';
  if (m.includes('membership')) return 'barcodeMembershipNo';
  if (m.includes('already has a head')) return 'relationToHead';
  return null;
};

const MemberManagement = ({ isDarkMode, seedFamilyId, editHeadFamilyId, headerSwitch, onPendingChanged = undefined, onOpenPending = undefined, openMember = null }) => {
  const styles = useStyles(isDarkMode);
  const t = styles.t;
  // const readOnly = useReadOnly(); // only the commented-out photo Remove button used this

  // When the merged screen opens this via "Add member" on a family, start on the
  // registration form with that family already linked. Otherwise start on the list.
  const [activeView, setActiveView] = useState(seedFamilyId ? 'registration' : 'directory');

  // The one Add creates either a Head (with a brand-new family) or a Member of an
  // existing family. A plain +Add opens on Head; arriving from a family's
  // "Add member" forces Member with that family linked.
  const [addMode, setAddMode] = useState<'head' | 'member'>(seedFamilyId ? 'member' : 'head');
  // Family-level fields Head mode needs; the head's own name/gender/mobile come
  // from the member fields.
  const [newFamily, setNewFamily] = useState({ parish: '', address: '' });
  // Set when editing a family from the Families tab: the head and the family are
  // edited together here. Stays set whether or not a head member existed yet.
  const [editingFamilyId, setEditingFamilyId] = useState<string | null>(null);
  const [formData, setFormData] = useState<MemberForm>(
    seedFamilyId ? { ...EMPTY_FORM, familyId: seedFamilyId } : EMPTY_FORM
  );
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewingMember, setViewingMember] = useState(null);
  /** Certificate opened in the in-app preview: { label, fileName, dataUrl }. */
  const [previewDoc, setPreviewDoc] = useState(null);
  /** Members awaiting confirmation for the two irreversible drawer actions. */
  const [pendingPromote, setPendingPromote] = useState(null);
  /** Guest actions awaiting confirmation: take onto the roll / stay ended / extend. */
  const [pendingConvert, setPendingConvert] = useState(null);
  const [pendingLeave, setPendingLeave] = useState(null);
  const [pendingExtend, setPendingExtend] = useState(null);
  const [extendUntil, setExtendUntil] = useState('');
  /** Which slice of the Guests tab is showing. */
  const [guestFilter, setGuestFilter] = useState<GuestFilter>('guest');
  /** Wrong entry awaiting the removal reason (System Administrator only). */
  const [pendingDelete, setPendingDelete] = useState(null);
  /** Member whose life status is being changed, with their family's live members. */
  const [statusChange, setStatusChange] = useState<{ member: Member; family: Member[] } | null>(null);
  const admin = isAdmin(getStoredUser());
  /** Inline field errors, keyed by form field name. */
  const [errors, setErrors] = useState<Record<string, string>>({});
  // The member being edited is the family head (so 'Head' stays selectable).
  const [editingWasHead, setEditingWasHead] = useState(false);
  // Likely double entries the server found; the user may save anyway.
  const [dupWarnings, setDupWarnings] = useState<DuplicateItem[] | null>(null);
  /** Disables the async menu items on the row currently acting. */
  const [busyRowId, setBusyRowId] = useState(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  /** Full-page document preview: { members }. */
  const [preview, setPreview] = useState(null);
  /** Shown after Add Head: the new family went for approval under this reference. */
  const [sentFamily, setSentFamily] =
    useState<{ referenceNo: string; headName: string; waitingFor: string[] } | null>(null);

  const isEditing = editingId !== null;
  const { notification, notify, dismiss } = useNotification();
  const memberStatuses = useOptionList('church_member_statuses');
  // A parish can turn member document uploads off in Settings. Undefined (older
  // config) counts as on, so nothing disappears without an explicit choice.
  const churchConfig = useChurchConfig();
  const documentsEnabled = churchConfig.documentUploads !== false;
  /** How long a new guest stay runs unless told otherwise (Settings › Rules). */
  const guestMonths = Number(churchConfig.guestPeriodMonths) > 0
    ? Number(churchConfig.guestPeriodMonths)
    : DEFAULT_GUEST_MONTHS;

  // Families load once as a light lookup (no photos). The Add form's
  // "link to family" list, the head-name labels and the family editor all need
  // the whole set, and a family row is small.
  const { data: families, refetch: refetchFamilies } = useFetchList<Family>(
    (p, o) => api.get(familiesApi.url(), o).then(getList)
  );

  // Only LIVE families are offered as a link target — a deleted household must
  // not still appear in "Link to Family ID" (CHM-005). The full `families` set
  // above is kept for the family editor and head-name lookups, which need voided
  // rows too.
  const familyLabels = useMemo(
    () => families.filter((f) => !isVoided(f)).map((f) => `${familyIdOf(f)} - ${f.headOfFamily}`),
    [families]
  );

  // The members list is server-paginated: the backend returns only the current
  // page (basic columns, no photos) for the active tab and search. The full
  // record is fetched by id when a member is opened or edited — so the
  // browser never downloads every member.
  const PAGE_SIZE = getPreferences().pageSize;
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [pageNum, setPageNum] = useState(1);
  const clear = useCallback(() => setSearchTerm(''), []);

  // A new tab or a new search always starts back on page 1.
  useEffect(() => { setPageNum(1); }, [activeView, guestFilter, debouncedSearch]);

  // The Guests tab narrows further by where the stay stands.
  const bucket = activeView === 'guest' ? guestFilter : activeView;
  const { data: members, raw: pageRaw, loading, refetch: refetchPage } =
    useFetchList<Member>((p, o) => memberList.page(p, o), {
      params: { bucket, q: debouncedSearch, page: pageNum - 1, size: PAGE_SIZE }
    });
  const totalItems = getTotal(pageRaw);
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  // Tab counts and the member-status tab list come from the backend, so the
  // client never has to hold every member just to label the tabs.
  const [countsData, setCountsData] = useState<{
    directory: number; inactive: number; deceased: number; transferred: number; void: number;
    guests: number; guestsExpiring: number; guestsExpired: number;
    memberStatus: Record<string, number>;
  }>({
    directory: 0, inactive: 0, deceased: 0, transferred: 0, void: 0,
    guests: 0, guestsExpiring: 0, guestsExpired: 0, memberStatus: {}
  });

  const refetchCounts = useCallback(async () => {
    try {
      const c = await memberList.counts();
      setCountsData({
        directory: Number(c.directory) || 0,
        inactive: Number(c.inactive) || 0,
        deceased: Number(c.deceased) || 0,
        transferred: Number(c.transferred) || 0,
        void: Number(c.void) || 0,
        guests: Number(c.guests) || 0,
        guestsExpiring: Number(c.guestsExpiring) || 0,
        guestsExpired: Number(c.guestsExpired) || 0,
        memberStatus: (c.memberStatus as Record<string, number>) || {}
      });
    } catch { /* keep the last counts on a transient failure */ }
  }, []);
  useEffect(() => { refetchCounts(); }, [refetchCounts]);

  /**
   * One extra tab per Member Status actually in use (e.g. "NRI"), taken from the
   * backend counts. Reserved keys are excluded so a category typed as "void" or
   * "directory" cannot shadow a real view.
   */
  const memberStatusTabs = useMemo(
    () => Object.keys(countsData.memberStatus)
      .filter((s) => s && !RESERVED_TABS.includes(String(s).toLowerCase()))
      .sort(),
    [countsData]
  );

  const counts = useMemo(() => ({
    directory: countsData.directory,
    inactive: countsData.inactive,
    deceased: countsData.deceased,
    transferred: countsData.transferred,
    void: countsData.void,
    guests: countsData.guests,
    guestsExpiring: countsData.guestsExpiring,
    guestsExpired: countsData.guestsExpired,
    ...countsData.memberStatus
  }), [countsData]);

  // A mutation moves the current page, the tab counts and the family lookup
  // together, so one refetch refreshes all three.
  const refetch = useCallback(async () => {
    await Promise.all([refetchPage(), refetchCounts(), refetchFamilies()]);
  }, [refetchPage, refetchCounts, refetchFamilies]);

  // The server already applied the tab and the search, so the page rows are what
  // we render. This object stands in for usePagination's shape so <Pagination>
  // and the selection helpers keep working unchanged.
  const page = {
    currentItems: members,
    currentPage: pageNum,
    totalPages,
    totalItems,
    itemsPerPage: PAGE_SIZE,
    setCurrentPage: setPageNum,
    next: () => setPageNum((p) => Math.min(p + 1, totalPages)),
    prev: () => setPageNum((p) => Math.max(p - 1, 1)),
    isFirst: pageNum <= 1,
    isLast: pageNum >= totalPages,
    reset: () => setPageNum(1)
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setEditingWasHead(false);
    setErrors({});
    setNewFamily({ parish: '', address: '' });
    setEditingFamilyId(null);
    setAddMode('head');
  };

  const switchView = (key) => {
    if (key !== 'registration') resetForm();
    setActiveView(key);
    page.reset();
    clear();
    // A selection made in Active and then acted on from Deleted History
    // would be baffling, so it does not survive a tab change.
    setSelectedIds([]);
  };

  const familyRefFor = (familyId) => {
    const fam = families.find((f) => familyIdOf(f) === familyId);
    return fam ? `${familyIdOf(fam)} - ${fam.headOfFamily}` : familyId || '';
  };

  /**
   * The head of the household a member belongs to. A member row stores only the
   * family id, so "FAM-118" on its own says nothing about whose house it is —
   * the name has to be looked up from the family register, both on screen and
   * on paper.
   */
  const headNameFor = (familyId?: string): string => {
    const fam = families.find((f) => familyIdOf(f) === familyId);
    return String(fam?.headOfFamily || '');
  };

  /** Member rows carrying their head's name, for printing. */
  const withHead = (rows: Member[]) =>
    rows.map((m) => ({ ...m, headOfFamily: headNameFor(m.familyId) }));

  // List rows are light (basic columns, no photo or documents). Before viewing,
  // editing a member we fetch the full record by id so those flows
  // have the photo and documents. Falls back to the light row if the call fails.
  /**
   * The list rows are a light summary (no photo, documents, education...), and
   * update() replaces the whole record. Anything that is going to be SENT BACK
   * must start from the full record — and must stop if it cannot be loaded,
   * because saving the summary in its place blanks every omitted field
   * (CHM-004). Viewing may fall back to the summary.
   */
  const fetchFull = async (row) => {
    if (!row || row.__kind === 'family' || row.id == null) return row;
    try {
      const full = await membersApi.get(row.id);
      return full ? { ...full, __kind: 'member' } : row;
    } catch {
      return row;
    }
  };
  const loadForWrite = async (row) => {
    if (!row || row.__kind === 'family' || row.id == null) return row;
    const full = await membersApi.get(row.id);
    if (!full) throw new Error('Could not load the full member record. Nothing was changed.');
    return { ...full, __kind: 'member' };
  };

  // Open the read-only drawer with the member's full record.
  const viewMember = async (row) => {
    setViewingMember(await fetchFull(row));
  };

  // Arrived from a fellowship's member list: open that member straight away,
  // then drop the hand-over so a refresh shows the plain directory.
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const id = (location.state as { viewMemberId?: number } | null)?.viewMemberId;
    if (id == null) return;
    viewMember({ id, __kind: 'member' });
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Opened from the Approvals inbox ("Open the record"): the member, already loaded.
  useEffect(() => {
    if (openMember) setViewingMember({ ...openMember, __kind: 'member' });
  }, [openMember]);

  const startEdit = async (row) => {
    let member;
    try {
      member = await loadForWrite(row);
    } catch (err) {
      notify(err.message || 'Could not load the member record.', 'error');
      return;
    }
    setFormData({ ...EMPTY_FORM, ...member });
    setEditingId(member.id);
    setEditingWasHead(isHead(member.relationToHead));
    // Editing an existing member is always Member mode — the family exists.
    setAddMode('member');
    // A head owns the family, so editing one also edits the family's Address /
    // Parish on this same page — load them so the fields show the current values.
    if (isHead(member.relationToHead)) {
      const fam = families.find((f) => familyIdOf(f) === member.familyId);
      setNewFamily({ parish: fam?.parish || '', address: fam?.address || '' });
    } else {
      setNewFamily({ parish: '', address: '' });
    }
    setActiveView('registration');
    setViewingMember(null);
  };

  /**
   * Edit a family (and its head) on this page — the same form a head is created
   * on. If the family already has a head member, that member is edited; if not
   * (older or seeded families), the head is seeded from the family's own head
   * fields and created on save. Either way the family's Address / Parish are
   * editable here and updated together.
   */
  const openFamilyEdit = async (famKey, fam, head) => {
    if (head) {
      let fullHead;
      try {
        fullHead = await loadForWrite(head);
      } catch (err) {
        notify(err.message || 'Could not load the head member record.', 'error');
        return;
      }
      setFormData({ ...EMPTY_FORM, ...fullHead });
      setEditingId(fullHead.id);
    } else {
      setFormData({
        ...EMPTY_FORM,
        name: fam?.headOfFamily || '',
        gender: (fam?.headGender || '') as Member['gender'],
        mobileNumber: fam?.contactNumber || fam?.contact || '',
        barcodeMembershipNo: fam?.membershipNo || '',
        relationToHead: 'Head',
        familyId: famKey,
        status: 'Alive'
      });
      setEditingId(null);
    }
    setEditingFamilyId(famKey);
    setNewFamily({ parish: fam?.parish || '', address: fam?.address || '' });
    setAddMode('member');
    setErrors({});
    setActiveView('registration');
    setViewingMember(null);
  };

  // Opened from the Families tab's Edit: once the data has loaded, open that
  // family (and its head, if any) in the edit form.
  const consumedHeadEdit = useRef(false);
  useEffect(() => {
    if (!editHeadFamilyId || consumedHeadEdit.current) return;
    if (families.length === 0) return; // wait for the family lookup to arrive
    consumedHeadEdit.current = true;
    const fam = families.find((f) => familyIdOf(f) === editHeadFamilyId);
    // The head is looked up from just this family's members, not the whole list.
    (async () => {
      const famMembers = getList<Member>(await memberList.byFamily(editHeadFamilyId).catch(() => []));
      const head = famMembers.find((m) => !isVoided(m) && isHead(m.relationToHead));
      openFamilyEdit(editHeadFamilyId, fam, head);
    })();
    // openFamilyEdit is a stable closure; re-run only when the data arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editHeadFamilyId, families]);

  const setField = (name) => (e) => {
    const { value } = e.target;
    // Typing in a field clears its own error, so the form stops nagging about
    // something the user is already fixing.
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));

    if (name === 'maritalStatus') {
      // Single (or a cleared status) has no wedding, so the Date of Marriage
      // field goes away — and any date already typed goes with it, rather than
      // staying behind in a hidden field and being saved.
      setFormData((prev) => ({
        ...prev,
        maritalStatus: value,
        dateOfMarriage: hasMarriageDate(value) ? prev.dateOfMarriage : ''
      }));
    } else if (name === 'mobileNumber') {
      setFormData((prev) => ({ ...prev, mobileNumber: value.replace(/\D/g, '').slice(0, 10) }));
    } else if (name === 'aadhaarNumber') {
      setFormData((prev) => ({ ...prev, aadhaarNumber: value.replace(/\D/g, '').slice(0, 12) }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  /** Family-level fields shown only in Head mode. */
  const setFamilyField = (name) => (e) => {
    const { value } = e.target;
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    setNewFamily((prev) => ({ ...prev, [name]: value }));
  };

  const isHeadMode = addMode === 'head' && !isEditing;
  // Editing a family (from the Families tab) edits its head here too.
  const isFamilyEdit = Boolean(editingFamilyId);
  // Editing the head of a family also edits the family, on this same page.
  const editingHead = isEditing && isHead(formData.relationToHead);
  // The family's Address / Parish show for a new head, an existing head, and a
  // family edit; a regular member picks a family link instead.
  const showFamilyFields = isHeadMode || editingHead || isFamilyEdit;
  // The family a member is being linked to — its address is shown, not retyped.
  const linkedFamily = showFamilyFields
    ? null
    : families.find((f) => familyIdOf(f) === String(formData.familyId || '').split(' - ')[0].trim()) || null;

  /** `confirmed`: the user chose "Save anyway" on the duplicate warnings. */
  const handleSubmit = async (e?, confirmed = false) => {
    e?.preventDefault?.();

    // Duplicate checks (mobile / Aadhaar / membership no.) now run on the server,
    // which holds every member; the client no longer does. So no member list is
    // passed here — validateMember still checks the required fields, formats and
    // that the linked family exists.
    const found = validateMember(
      formData, [], families, editingId,
      showFamilyFields ? { skipFamilyLink: true, requireMobile: true } : undefined
    );
    // Address belongs to the family, not the member, so it is checked here —
    // whenever the family fields are on show (new head or an edited head).
    if (showFamilyFields && !String(newFamily.address || '').trim()) {
      found.address = 'The family address is required.';
    }
    if (Object.keys(found).length) {
      setErrors(found);
      notify('Please correct the highlighted fields.', 'error');
      return;
    }
    setErrors({});

    // A head recorded as Single has no spouse and no children on the register,
    // so those relations cannot be attached to them (CHM-143). Checked against
    // the family's own members rather than the light family lookup, which does
    // not carry the head's marital status.
    if (!isHeadMode) {
      const relation = String(formData.relationToHead || '').trim().toLowerCase();
      if (['spouse', 'wife', 'husband', 'son', 'daughter'].includes(relation)) {
        const famId = familyIdOf(formData.familyId);
        const famMembers = getList<Member>(await memberList.byFamily(famId).catch(() => []));
        const head = famMembers.find(
          (m) => !isVoided(m) && isHead(m.relationToHead) && m.id !== editingId
        );
        if (head && String(head.maritalStatus || '').trim() === 'Single') {
          setErrors({ relationToHead: `The head is recorded as Single, so no ${relation} can be added.` });
          notify(`${head.name || 'The family head'} is recorded as Single. Update their marital status before adding a ${relation}.`, 'error');
          return;
        }
      }
    }

    // Saved once, used by both save paths below: an older record edited down to
    // Single keeps its marriage date in the (now hidden) field, and it must not
    // be written back.
    const marriageDate = hasMarriageDate(formData.maritalStatus)
      ? formData.dateOfMarriage
      : '';

    // Ask the server about duplicates before anything is written — in Head
    // mode the family is created first, so a refusal after that would leave
    // an empty family behind. Errors stop here; warnings ask "Save anyway?".
    if (!confirmed) {
      const check = await memberList.duplicateCheck(
        stripKind({ ...formData, familyId: isHeadMode ? '' : familyIdOf(formData.familyId) }),
        isEditing ? editingId : null
      ).catch(() => null); // unreachable check: the save itself still enforces the hard rules
      if (check?.errors?.length) {
        setErrors(Object.fromEntries(check.errors.map((x) => [x.field || 'name', x.message])));
        notify(check.errors[0].message, 'error');
        return;
      }
      if (check?.warnings?.length) {
        setErrors(Object.fromEntries(check.warnings.map((x) => [x.field || 'name', x.message])));
        setDupWarnings(check.warnings);
        return;
      }
    }
    setDupWarnings(null);
    setErrors({});

    const memberStatus = String(formData.memberStatus || '').trim();
    if (memberStatus) memberStatuses.ensure(memberStatus);

    // --- Head mode: a new family ---
    // Sent as a registration. When Settings › Approvals › Members & Families ›
    // "Adding a new record" is on, it waits there and the last approval creates
    // the family and its head (sharing one Membership No.), exactly as a public
    // registration does; when it is off, the server adds it at once.
    if (isHeadMode) {
      const name = String(formData.name || '').trim();
      setSaving(true);
      try {
        const head: RegistrationPerson = {
          status: formData.status || 'Alive',
          dateOfDeath: formData.dateOfDeath, transferChurch: formData.transferChurch,
          memberStatus: formData.memberStatus,
          barcodeMembershipNo: String(formData.barcodeMembershipNo || '').trim(),
          guestFrom: formData.guestFrom, guestUntil: formData.guestUntil,
          guestOrigin: formData.guestOrigin, guestPurpose: formData.guestPurpose,
          name, relativeName: formData.relativeName, relationToHead: 'Head',
          gender: formData.gender || '', location: formData.location,
          dateOfBirth: formData.dateOfBirth || '', maritalStatus: formData.maritalStatus || '',
          dateOfMarriage: marriageDate, bloodGroup: formData.bloodGroup,
          education: formData.education, occupation: formData.occupation,
          mobileNumber: formData.mobileNumber, aadhaarNumber: formData.aadhaarNumber,
          baptismDate: formData.baptismDate, confirmationDate: formData.confirmationDate,
          communionStatus: formData.communionStatus,
          baptismCert: formData.baptismCert, baptismCertUrl: formData.baptismCertUrl,
          marriageCert: formData.marriageCert, marriageCertUrl: formData.marriageCertUrl,
          membershipCert: formData.membershipCert, membershipCertUrl: formData.membershipCertUrl,
          idProof: formData.idProof, idProofUrl: formData.idProofUrl
        };
        const sent = await memberRegistrations.create({
          head, address: newFamily.address, parish: newFamily.parish, members: []
        });
        onPendingChanged?.();

        resetForm();
        setActiveView('directory');
        if (sent.status === 'APPROVED') {
          // No approval needed: the family is on the register already.
          await refetch();
          notify(`Family ${sent.familyId || ''} created with ${name} as head.`, 'success');
        } else {
          setSentFamily({
            referenceNo: sent.referenceNo,
            headName: name,
            waitingFor: sent.approval?.waitingFor || []
          });
          window.dispatchEvent(new Event('approvals-changed'));
        }
      } catch (err) {
        const field = dupFieldFor(err.message);
        if (field) setErrors((prev) => ({ ...prev, [field]: err.message }));
        notify(err.message || 'Could not save the new family.', 'error');
      } finally {
        setSaving(false);
      }
      return;
    }

    // --- Member mode: add a member to an existing family (or edit one) ---
    const familyId = String(formData.familyId).split(' - ')[0].trim();

    const payload = {
      ...formData,
      dateOfMarriage: marriageDate,
      // The gender <Select> starts blank; an unset value is sent as undefined
      // rather than an empty string.
      gender: (formData.gender || undefined) as Member['gender'],
      familyId
      // Member ID and (blank) Membership No. are generated by the backend on
      // create; on edit the loaded record already carries them in formData.
    };

    // A family cannot be headed by someone who has died or moved away.
    let demotedHead = false;
    if (['Deceased', 'Transferred'].includes(payload.status) && isHead(payload.relationToHead)) {
      payload.relationToHead = 'Other';
      demotedHead = true;
    }

    setSaving(true);
    try {
      // Promoting someone to Head demotes the previous one and renames the family —
      // all saved together (or sent for approval together).
      const together: BatchOperation[] = [];
      if (isHead(payload.relationToHead)) {
        // Look the previous head up from just this family's members (live only —
        // a deleted ex-head must not shadow the promotion).
        const famMembers = getList<Member>(await memberList.byFamily(familyId).catch(() => []));
        const previousHead = famMembers.find(
          (m) => isHead(m.relationToHead) && m.id !== editingId && !isVoided(m)
        );
        if (previousHead) {
          together.push(batch.change(membersApi, previousHead.id, { ...stripKind(previousHead), relationToHead: 'Other' }));
        }

        const family = families.find((f) => familyIdOf(f) === familyId);
        if (family) {
          together.push(batch.change(familiesApi, family.id, {
            ...stripKind(family),
            headOfFamily: payload.name,
            headGender: payload.gender || family.headGender,
            familyName: family.familyName || `${payload.name}'s Family`,
            contact: payload.mobileNumber || family.contactNumber || family.contact,
            contactNumber: payload.mobileNumber || family.contactNumber || family.contact,
            // Editing a head or a family carries the Address / Parish and
            // Membership No. shown on this page. (A plain promote-to-head leaves
            // them as they are.)
            ...(editingHead || isFamilyEdit
              ? {
                  address: newFamily.address,
                  parish: newFamily.parish,
                  membershipNo: payload.barcodeMembershipNo || family.membershipNo
                }
              : {})
          }));
        }
      }

      if (together.length) {
        together.push(isEditing
          ? batch.change(membersApi, editingId, stripKind(payload))
          : batch.add(membersApi, stripKind(payload)));
        await batch.run(together, `${payload.name} becomes Head of Family ${familyId}`);
      } else if (isEditing) await membersApi.update(editingId, stripKind(payload));
      else await membersApi.create(stripKind(payload));

      await refetch();

      notify(
        demotedHead
          ? `Member marked as ${formData.status}. Relation changed from 'Head' to 'Other' — please assign a new Head.`
          : isEditing ? 'Member updated successfully.' : 'Member saved successfully.',
        'success'
      );

      resetForm();
      setActiveView(viewFor(payload));
    } catch (err) {
      const field = dupFieldFor(err.message);
      if (field) setErrors((prev) => ({ ...prev, [field]: err.message }));
      notify(err.message || 'Could not save the member record.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Makes a member the head of their family: demotes whoever held it, then
   * renames the family to match — saved together (or sent for approval
   * together). Mirrors the promotion branch of handleSubmit, but reachable from
   * the drawer without opening the edit form.
   */
  const promoteToHead = async (member) => {
    const familyId = member.familyId;
    setSaving(true);
    try {
      // Look up this family's current head (live only — a deleted ex-head must
      // not shadow the promotion).
      const famMembers = getList<Member>(await memberList.byFamily(familyId).catch(() => []));
      const previousHead = famMembers.find(
        (m) => isHead(m.relationToHead) && m.id !== member.id && !isVoided(m)
      );
      const together: BatchOperation[] = [];
      if (previousHead) {
        together.push(batch.change(membersApi, previousHead.id, { ...stripKind(previousHead), relationToHead: 'Other' }));
      }
      together.push(batch.change(membersApi, member.id, { ...stripKind(member), relationToHead: 'Head' }));
      const family = families.find((f) => familyIdOf(f) === familyId);
      if (family) {
        together.push(batch.change(familiesApi, family.id, {
          ...stripKind(family),
          headOfFamily: member.name,
          familyName: family.familyName || `${member.name}'s Family`,
          contact: family.contactNumber || family.contact || member.mobileNumber
        }));
      }
      await batch.run(together, `${member.name} becomes Head of Family ${familyId}`);

      await refetch();
      setViewingMember(null);
      notify(`${member.name} is now Head of Family.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not promote the member.', 'error');
    } finally {
      setSaving(false);
      setPendingPromote(null);
    }
  };

  // --- Guest stay actions ---
  // Each fetches the full record first: the list row is the light summary and
  // update() is a full replace, so sending a row back would blank the photo
  // and documents.

  /** Takes a guest onto the roll: Regular from today, the stay kept as history. */
  const convertGuest = async (row) => {
    setSaving(true);
    try {
      const member = await loadForWrite(row);
      await membersApi.update(member.id, {
        ...stripKind(member), memberStatus: ROLL_STATUS, membershipDate: todayISO()
      });
      memberStatuses.ensure(ROLL_STATUS);
      await refetch();
      setViewingMember(null);
      setActiveView('directory');
      notify(`${member.name} is now a permanent member.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not update the member.', 'error');
    } finally {
      setSaving(false);
      setPendingConvert(null);
    }
  };

  /** The stay is over and the guest has gone home: Transferred, to their home church. */
  const guestLeft = async (row) => {
    setSaving(true);
    try {
      const member = await loadForWrite(row);
      await membersApi.update(member.id, {
        ...stripKind(member),
        status: 'Transferred',
        transferChurch: member.transferChurch || member.guestOrigin || 'Returned home (guest stay ended)'
      });
      await refetch();
      setViewingMember(null);
      setActiveView('transferred');
      notify(`${member.name} marked as left — moved to Transferred.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not update the member.', 'error');
    } finally {
      setSaving(false);
      setPendingLeave(null);
    }
  };

  /** Opens the extend dialog with the current end date (or today) as the base. */
  const requestExtend = (row) => {
    setExtendUntil(row.guestUntil || todayISO());
    setPendingExtend(row);
  };

  const extendGuest = async () => {
    const row = pendingExtend;
    if (!row) return;
    if (!extendUntil || extendUntil <= todayISO()) {
      notify('Pick a date after today.', 'error');
      return;
    }
    setSaving(true);
    try {
      const member = await loadForWrite(row);
      await membersApi.update(member.id, { ...stripKind(member), guestUntil: extendUntil });
      await refetch();
      setViewingMember((v) => (v && v.id === member.id ? { ...v, guestUntil: extendUntil } : v));
      notify(`${member.name}'s stay now runs until ${formatDate(extendUntil)}.`, 'success');
      setPendingExtend(null);
    } catch (err) {
      notify(err.message || 'Could not extend the stay.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Soft delete. The record keeps its life status and every other field, so a
   * restore returns the member exactly as they were.
   *
   * The whole row is sent because `MemberService.update()` is `setId` + `save()`
   * — a full replace. A partial payload would null every column it omits.
   */
  /** Voiding one member, as a save. The list row is a light summary, so the full record is fetched and sent back whole. */
  const voidMemberOp = async (row, reason): Promise<BatchOperation> => {
    const member = await loadForWrite(row);
    return batch.change(membersApi, member.id, {
      ...stripKind(member),
      recordStatus: 'VOID',
      deleteReason: reason,
      deletedAt: stamp()
    });
  };

  /** Voids one member. */
  const voidMember = async (row, reason) => {
    const op = await voidMemberOp(row, reason);
    await api.put(op.path, op.body);
  };

  const afterDelete = async (member) => {
    await refetch();
    // The Add form's "Link to Family ID" reads the family rows, so they must
    // reflect a new head or a deleted family straight away.
    await refetchFamilies();
    setSelectedIds((prev) => prev.filter((id) => id !== member.id));
    setViewingMember(null);
  };

  const handleVoid = async (reason) => {
    const member = pendingDelete;
    if (!member) return;

    setSaving(true);
    try {
      await voidMember(member, reason);
      await afterDelete(member);
      notify(`${member.name} moved to Deleted History.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not delete the member.', 'error');
    } finally {
      setSaving(false);
      setPendingDelete(null);
    }
  };

  /**
   * Brings a deleted member back. Their life status was preserved, so they
   * return to whichever tab they came from — not blindly to Active.
   *
   * Relation to Head is deliberately not restored beyond what was saved: if
   * they were Head, someone else may hold it now, and this cannot know.
   */
  const handleUndelete = async (row) => {
    setSaving(true);
    try {
      // The list row is the light summary; update() replaces the whole record,
      // so the full one (photo, documents) is fetched before it is sent back.
      const member = await loadForWrite(row);
      await membersApi.update(member.id, {
        ...stripKind(member), recordStatus: 'ACTIVE', deleteReason: null, deletedAt: null
      });
      await refetch();
      setActiveView(viewFor(member));
      notify(`${member.name} restored.`, 'success');
    } catch (err) {
      notify(err.message || 'Could not restore the member.', 'error');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Nobody is deleted for leaving or passing away — their status changes.
   * "Remove wrong entry" is only for a record made by mistake: a System
   * Administrator, with a reason; the server refuses a record with church
   * history, or a head whose family still has other members.
   */
  const requestDelete = (member) => setPendingDelete(member);

  /** Opens Change status with the family's live members (to choose a new head). */
  const openChangeStatus = async (row) => {
    const famMembers = getList<Member>(await memberList.byFamily(row.familyId).catch(() => []))
      .filter((m) => !isVoided(m));
    setStatusChange({ member: row, family: famMembers });
  };



  const currentIds = page.currentItems.map((m) => m.id);
  const isAllCurrentSelected = currentIds.length > 0 && currentIds.every((id) => selectedIds.includes(id));

  const toggleSelectAll = (checked) =>
    setSelectedIds((prev) => (checked
      ? Array.from(new Set([...prev, ...currentIds]))
      : prev.filter((id) => !currentIds.includes(id))));

  const toggleSelectOne = (id, checked) =>
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((x) => x !== id)));

  const selectColumn = {
    key: 'select',
    header: (
      <input
        type="checkbox"
        checked={isAllCurrentSelected}
        onChange={(e) => toggleSelectAll(e.target.checked)}
        aria-label="Select all on this page"
        style={{ cursor: 'pointer' }}
      />
    ),
    width: '40px',
    render: (m) => (
      // DataTable puts its click handler on the <tr>; without this, ticking a
      // box would also open the record.
      <span onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selectedIds.includes(m.id)}
          onChange={(e) => toggleSelectOne(m.id, e.target.checked)}
          aria-label={`Select ${m.name}`}
          style={{ cursor: 'pointer' }}
        />
      </span>
    )
  };

  const actionsColumn = {
    key: 'actions',
    header: 'Action',
    width: '56px',
    align: 'right' as const,
    nowrap: true,
    render: (m) => {
      const busy = busyRowId === m.id;
      return (
        // Same reason as the checkbox: keep the menu from opening the record.
        <span onClick={(e) => e.stopPropagation()}>
          <RowActions
            isDarkMode={isDarkMode}
            items={isVoided(m)
              ? [{ icon: '♻️', label: 'Restore', onClick: () => handleUndelete(m) }]
              : [
                  { icon: '👁️', label: 'View', onClick: () => viewMember(m) },
                  { icon: '✏️', label: 'Edit', write: true, onClick: () => startEdit(m) },
                  // A living guest can have the stay extended, be taken onto
                  // the roll, or be marked as gone home.
                  ...(isGuest(m) && lifeOf(m) === 'Alive' ? [
                    { icon: '📅', label: 'Extend stay', write: true, separated: true, onClick: () => requestExtend(m) },
                    { icon: '⭐', label: 'Make permanent member', write: true, onClick: () => setPendingConvert(m) },
                    { icon: '🚪', label: 'Guest left', write: true, onClick: () => setPendingLeave(m) }
                  ] : []),
                  { icon: '🔄', label: 'Change status', write: true, separated: true, onClick: () => openChangeStatus(m) },
                  ...(admin ? [{ icon: '🗑️', label: 'Remove wrong entry', danger: true, onClick: () => requestDelete(m) }] : [])
                ]}
          />
        </span>
      );
    }
  };

  const columnsByView = {
    directory: [
      { key: 'memberId', header: 'Member ID', nowrap: true, render: (m) => <strong>{m.memberId}</strong> },
      { key: 'name', header: 'Name' },
      { key: 'relativeName', header: 'S/o, D/o, W/o', render: (m) => m.relativeName || '-' },
      { key: 'familyId', header: 'Family ID', nowrap: true },
      { key: 'headOfFamily', header: 'Head of Family', render: (m) => headNameFor(m.familyId) || '-' },
      { key: 'relationToHead', header: 'Relation' },
      { key: 'location', header: 'Location', render: (m) => m.location || '-' },
      { key: 'memberStatus', header: 'Status', render: (m) => m.memberStatus || '-' },
      {
        key: 'dateOfBirth',
        header: 'DOB / Age',
        nowrap: true,
        render: (m) => (
          <>
            {formatDate(m.dateOfBirth)}
            <div style={{ fontSize: '11px', color: t.textFaint }}>Age: {ageFrom(m.dateOfBirth)}</div>
          </>
        )
      },
      { key: 'mobileNumber', header: 'Mobile', render: (m) => m.mobileNumber || '-' },
      actionsColumn
    ],
    deceased: [
      { key: 'memberId', header: 'Member ID', nowrap: true, render: (m) => <strong>{m.memberId}</strong> },
      { key: 'name', header: 'Name' },
      { key: 'familyId', header: 'Family ID', nowrap: true },
      { key: 'headOfFamily', header: 'Head of Family', render: (m) => headNameFor(m.familyId) || '-' },
      { key: 'dateOfBirth', header: 'Date of Birth', nowrap: true, render: (m) => formatDate(m.dateOfBirth) },
      { key: 'dateOfDeath', header: 'Date of Death', nowrap: true, render: (m) => formatDate(m.dateOfDeath) },
      actionsColumn
    ],
    transferred: [
      { key: 'memberId', header: 'Member ID', nowrap: true, render: (m) => <strong>{m.memberId}</strong> },
      { key: 'name', header: 'Name' },
      { key: 'familyId', header: 'Family ID', nowrap: true },
      { key: 'headOfFamily', header: 'Head of Family', render: (m) => headNameFor(m.familyId) || '-' },
      { key: 'transferChurch', header: 'Transferred To', render: (m) => m.transferChurch || '-' },
      actionsColumn
    ],
    guest: [
      { key: 'memberId', header: 'Member ID', nowrap: true, render: (m) => <strong>{m.memberId}</strong> },
      { key: 'name', header: 'Name' },
      { key: 'familyId', header: 'Family ID', nowrap: true },
      { key: 'headOfFamily', header: 'Head of Family', render: (m) => headNameFor(m.familyId) || '-' },
      { key: 'guestOrigin', header: 'Home Church / From', render: (m) => m.guestOrigin || '-' },
      { key: 'guestFrom', header: 'Since', nowrap: true, render: (m) => formatDate(m.guestFrom) },
      { key: 'guestUntil', header: 'Until', nowrap: true, render: (m) => formatDate(m.guestUntil) },
      {
        key: 'phase', header: 'Stay', nowrap: true,
        render: (m) => {
          const phase = guestPhase(m);
          return <Badge tone={GUEST_TONE[phase]}>{guestPhaseLabel(phase, daysUntil(m.guestUntil))}</Badge>;
        }
      },
      { key: 'mobileNumber', header: 'Mobile', render: (m) => m.mobileNumber || '-' },
      actionsColumn
    ],
    void: [
      { key: 'memberId', header: 'Member ID', nowrap: true, render: (m) => <strong>{m.memberId}</strong> },
      { key: 'name', header: 'Name' },
      { key: 'familyId', header: 'Family ID', nowrap: true },
      { key: 'headOfFamily', header: 'Head of Family', render: (m) => headNameFor(m.familyId) || '-' },
      // The life status they held when deleted, so a restore is predictable.
      { key: 'status', header: 'Was', render: (m) => lifeLabel(m.status) },
      { key: 'deletedAt', header: 'Deleted On', nowrap: true, render: (m) => m.deletedAt || '-' },
      { key: 'deleteReason', header: 'Reason', render: (m) => m.deleteReason || '-' },
      actionsColumn
    ]
  };

  // The select column is the same on every view, so it is prepended here rather
  // than repeated in each list above.
  const columns = [selectColumn, ...(columnsByView[activeView] || columnsByView.directory)];

  const isRegistration = activeView === 'registration';


  return (
    <div style={styles.container}>
      <Notification notification={notification} onClose={dismiss} />

      <PageHeader
        // Inside the merged screen the Families/Members switch is the header;
        // standalone it falls back to its own title.
        leading={headerSwitch}
        icon={headerSwitch ? undefined : '👥'}
        title={headerSwitch ? undefined : 'Member Management'}
        activeTab={activeView}
        onTabChange={switchView}
        // The header carries Deleted History and the Add action; the status
        // filters sit in the search row below, beside the field. While the form
        // is open these are hidden — just the switch and the form remain, so
        // nothing competes with what you're editing.
        tabs={isRegistration ? [] : [
          { key: 'void', label: `🗑️ Deleted History (${counts.void})`, variant: 'danger' },
          { key: 'registration', write: true, label: '+ Add New Entry' }
        ]}
      />

      {!isRegistration && (
        <>
          {/* One row: a compact search field with the status tabs beside it.
              Wraps on a narrow screen. */}
          <SearchBar
            value={searchTerm}
            onChange={(v) => { setSearchTerm(v); page.reset(); }}
            onClear={() => { clear(); page.reset(); }}
            placeholder="Search by Member ID, Name, Family ID or Mobile..."
            fieldStyle={{ flex: '0 1 320px', minWidth: '200px' }}
            style={{ flexWrap: 'wrap', alignItems: 'center' }}
            actions={
              <>
                <PrintButton
                  module="Members & Families"
                  documentType="MEMBER_DIRECTORY"
                  size="sm"
                  title="Member-Directory"
                  data={() => ({
                    title: 'Member Directory',
                    reference: `${activeView} members`,
                    rowCount: page.currentItems.length,
                    // Each member carries its household's head, which the row
                    // itself only references by id.
                    rows: withHead(page.currentItems)
                  })}
                  onNotify={notify}
                  isDarkMode={isDarkMode}
                />
              <SubTabs
                value={activeView}
                onChange={switchView}
                style={{ marginBottom: 0 }}
                tabs={[
                  { key: 'directory', label: `Active (${counts.directory})` },
                  { key: 'inactive', label: `Inactive (${counts.inactive})` },
                  { key: 'transferred', label: `Transferred (${counts.transferred})` },
                  { key: 'deceased', label: `Passed Away (${counts.deceased})` },
                  { key: 'guest', label: `Guests (${counts.guests})` },
                  ...memberStatusTabs.map((status) => ({ key: status, label: `${status} (${counts[status] || 0})` }))
                ]}
              />
              </>
            }
          />

          {activeView === 'guest' && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: t.spacing.md }}>
              {GUEST_FILTERS.map((f) => {
                const selected = guestFilter === f.key;
                return (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setGuestFilter(f.key)}
                    aria-pressed={selected}
                    style={{
                      padding: '7px 14px', minHeight: '36px', borderRadius: t.radius.pill,
                      fontWeight: 'bold', fontSize: t.fontSize.sm, cursor: 'pointer',
                      border: `1px solid ${selected ? t.primary : t.border}`,
                      backgroundColor: selected ? t.primarySoft : t.bgMain,
                      color: selected ? t.textAccent : t.textMuted
                    }}
                  >
                    {f.label} ({counts[f.countKey] || 0})
                  </button>
                );
              })}
            </div>
          )}

          <TablePanel
            title="Members"
            // whole screen went to paper instead of the document — the
            below={<Pagination {...page} itemsOnPage={page.currentItems.length} label="members" />}
            // Member Status tabs reuse the directory layout.
            columns={columns}
            rows={page.currentItems}
            loading={loading}
            emptyMessage={activeView === 'guest'
              ? 'No guests here.'
              : `No ${activeView === 'directory' ? 'active' : activeView} members found.`}
            onRowClick={(m) => viewMember(m)}
            rowStyle={(m) => (selectedIds.includes(m.id)
              ? { backgroundColor: isDarkMode ? '#2A3B4C' : '#E3F2FD' }
              : null)}
          />
        </>
      )}

      {isRegistration && (
        <FormLayout
          title={isFamilyEdit
            ? `Edit Family — ${editingFamilyId}`
            : isEditing ? `Edit Member: ${formData.memberId || ''}`
            : isHeadMode ? 'Add Head — creates a new family' : 'Add Member to a family'}
          submitLabel={isFamilyEdit
            ? 'Update Family + Head'
            : isEditing ? 'Update Member'
            : isHeadMode ? 'Save Family + Head' : 'Save Member'}
          submitting={saving}
          onSubmit={handleSubmit}
          onCancel={() => switchView('directory')}
          maxWidth="1000px"
        >
          {/* Top row: the Head/Member switch. The profile photo that used to sit
              on the right is commented out below. */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            gap: '16px', marginBottom: '18px'
          }}>
            <div style={{ flex: 1 }}>
              {/* The Head / Member switch, on the same page for both Add and Edit.
                  On Add you choose; on Edit it shows which one you're editing and
                  is locked (a head can't become a member from here). */}
              {(() => {
                const locked = isEditing || isFamilyEdit;
                const activeTab = locked ? (showFamilyFields ? 'head' : 'member') : addMode;
                return (
                  <div>
                    <div style={{ fontSize: t.fontSize.sm, fontWeight: t.fontWeight.bold, color: t.textMuted, marginBottom: '6px' }}>
                      {locked ? 'Editing' : 'Adding'}
                    </div>
                    <div style={{
                      display: 'inline-flex', gap: '4px', padding: '4px',
                      backgroundColor: t.bgSubtle, borderRadius: t.radius.pill, border: `1px solid ${t.border}`,
                      opacity: locked ? 0.85 : 1
                    }}>
                      {[
                        { key: 'head', label: '👤 Head' },
                        { key: 'member', label: '👥 Member' }
                      ].map(({ key, label }) => {
                        const active = activeTab === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            disabled={locked}
                            onClick={() => { if (!locked) { setAddMode(key as 'head' | 'member'); setErrors({}); } }}
                            aria-pressed={active}
                            title={locked && !active ? 'Locked while editing' : undefined}
                            style={{
                              padding: '8px 18px', borderRadius: t.radius.pill,
                              cursor: locked ? 'default' : 'pointer',
                              border: 'none', fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold,
                              backgroundColor: active ? t.primary : 'transparent',
                              color: active ? '#FFF' : t.textMuted,
                              // A locked, inactive tab fades back so the active one reads clearly.
                              ...(locked && !active ? { opacity: 0.5 } : {})
                            }}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    {/* One-line reminder of what this side means. */}
                    <div style={{ fontSize: t.fontSize.xs, color: t.textFaint, marginTop: '4px' }}>
                      {activeTab === 'head' ? 'Head — owns the family (family fields shown)' : 'Member — belongs to a family'}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Photo hidden from the UI on request. Kept here, commented out,
                so it can be restored. photo / photoDataUrl stay in form state
                and in the save path on purpose — update() is a full replace, so
                dropping them would blank a stored photo.

            <div style={{
              flexShrink: 0, width: '150px',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px'
            }}>
                  The whole picture is shown, never cropped — objectFit: contain
                  fits it inside the frame; a rounded square avoids the circular
                  crop that cut the edges off. Click it to view it full size.
              <div
                onClick={() => formData.photoDataUrl && setPreviewDoc({
                  label: 'Member Photo',
                  fileName: formData.photo || 'photo',
                  dataUrl: formData.photoDataUrl
                })}
                title={formData.photoDataUrl ? 'Click to view' : undefined}
                style={{
                  width: '120px', height: '120px', borderRadius: '12px',
                  border: `1px solid ${t.border}`, backgroundColor: t.bgSubtle,
                  overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: formData.photoDataUrl ? 'pointer' : 'default'
                }}
              >
                <img
                  src={formData.photoDataUrl || avatarUrl(formData.name, 'E3F2FD', '1976D2')}
                  alt={formData.name || 'Member'}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                />
              </div>
              <div style={{ width: '100%' }}>
                <FileInput
                  label="Photo"
                  accept="image/*"
                  fileName={formData.photo}
                  onFile={(name, dataUrl) => setFormData((prev) => ({ ...prev, photo: name, photoDataUrl: dataUrl }))}
                  onError={(msg) => notify(msg, 'error')}
                />
              </div>
                  View / Remove — only when a photo is actually uploaded.
              {formData.photoDataUrl && (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setPreviewDoc({
                      label: 'Member Photo',
                      fileName: formData.photo || 'photo',
                      dataUrl: formData.photoDataUrl
                    })}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: t.textAccent, fontSize: t.fontSize.sm, fontWeight: 'bold', textDecoration: 'underline'
                    }}
                  >
                    👁️ View
                  </button>
                      Remove is a write; a read-only user keeps View only (CHM-071).
                  {!readOnly && (
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, photo: '', photoDataUrl: '' }))}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      color: t.danger, fontSize: t.fontSize.sm, fontWeight: 'bold', textDecoration: 'underline'
                    }}
                  >
                    🗑️ Remove
                  </button>
                  )}
                </div>
              )}
            </div>
            */}
          </div>

          {/* The form runs in the order a person is described: which family,
              who they are, how to reach them, their place in the church, their
              sacraments, then papers. The public family registration form uses
              the same order, so staff and families see one layout. */}

          {/* 1. Family — a head owns it (and its address); a member joins one. */}
          <FormLayout.Section title="Family" />
          <FormLayout.Grid>
            {(isHeadMode || isFamilyEdit) ? (
              <Input label="Relation to Head" value="Head of the family" disabled onChange={() => {}} />
            ) : (
              <>
                {/* A head owns its family (create or edit), so there is no family
                    to pick — only a regular member links to one. */}
                {!showFamilyFields && (
                  <SearchableSelect
                    label="Family (search by head's name or Family ID)" required
                    allowAdd={false} allowManage={false}
                    placeholder="Search families..."
                    value={familyRefFor(formData.familyId) || formData.familyId}
                    onChange={(v) => {
                      setErrors((prev) => (prev.familyId ? { ...prev, familyId: undefined } : prev));
                      setFormData((prev) => ({ ...prev, familyId: String(v).split(' - ')[0].trim() }));
                    }}
                    options={familyLabels}
                    error={errors.familyId}
                  />
                )}
                <Select
                  label="Relation to Head" required options={editingWasHead ? RELATIONS : MEMBER_RELATIONS}
                  value={formData.relationToHead} onChange={setField('relationToHead')}
                  hint={editingWasHead ? undefined : "To make someone the head, use 'Promote to Head' on their record."}
                  error={errors.relationToHead}
                />
              </>
            )}
            {showFamilyFields ? (
              <>
                <Input
                  label="Family Address" required fullWidth rows={2}
                  placeholder="House no., street, area, city, PIN"
                  hint="The home address of the whole family — every member of this family shares it."
                  value={newFamily.address} onChange={setFamilyField('address')}
                  error={errors.address}
                />
                <Input
                  label="Parish" placeholder="e.g. St. Thomas"
                  value={newFamily.parish} onChange={setFamilyField('parish')}
                />
              </>
            ) : linkedFamily && (
              // A member lives at the family's address; it is shown, not typed
              // again, and changed by editing the head.
              <div style={{
                gridColumn: '1 / -1', padding: '10px 14px', borderRadius: t.radius.lg,
                backgroundColor: t.bgSubtle, border: `1px solid ${t.border}`, fontSize: t.fontSize.md
              }}>
                <div style={{ color: t.textMuted, fontSize: t.fontSize.sm, fontWeight: t.fontWeight.bold, marginBottom: '2px' }}>
                  🏠 Family Address (same as the head, {linkedFamily.headOfFamily})
                </div>
                <div style={{ color: t.textMain }}>
                  {linkedFamily.address || <em style={{ color: t.textFaint }}>No address recorded for this family</em>}
                  {linkedFamily.parish && <span style={{ color: t.textMuted }}> · Parish: {linkedFamily.parish}</span>}
                </div>
                <div style={{ color: t.textFaint, fontSize: t.fontSize.xs, marginTop: '2px' }}>
                  To change it, edit the head of this family.
                </div>
              </div>
            )}
          </FormLayout.Grid>

          {/* 2. Who they are. */}
          <FormLayout.Section title="Personal Details" />
          <FormLayout.Grid>
            <Input
              label="Full Name" required placeholder="e.g. John Samuel"
              value={formData.name} onChange={setField('name')}
              style={{ fontWeight: 'bold' }}
              error={errors.name}
            />
            <Input
              label="S/o, D/o, W/o" placeholder="Father's / husband's name"
              value={formData.relativeName} onChange={setField('relativeName')}
            />
            <Select
              label="Gender" required options={GENDERS}
              value={formData.gender} onChange={setField('gender')}
              error={errors.gender}
            />
            <Input
              label="Date of Birth" type="date" required
              value={formData.dateOfBirth} onChange={setField('dateOfBirth')}
              error={errors.dateOfBirth}
            />
            <Select
              label="Marital Status" required options={MARITAL_STATUSES}
              value={formData.maritalStatus} onChange={setField('maritalStatus')}
              error={errors.maritalStatus}
            />
            {/* Only for a status that implies a wedding — a Single member has
                no marriage date to record. */}
            {hasMarriageDate(formData.maritalStatus) && (
              <Input
                label="Date of Marriage" type="date"
                value={formData.dateOfMarriage} onChange={setField('dateOfMarriage')}
                error={errors.dateOfMarriage}
              />
            )}
            <Select
              label="Blood Group"
              options={withStoredValue(BLOOD_GROUPS, formData.bloodGroup)}
              value={formData.bloodGroup} onChange={setField('bloodGroup')}
            />
            <Input label="Education" placeholder="e.g. B.Sc" value={formData.education} onChange={setField('education')} />
            <Input label="Occupation" placeholder="e.g. Teacher" value={formData.occupation} onChange={setField('occupation')} />
          </FormLayout.Grid>

          {/* 3. How to reach and identify them. */}
          <FormLayout.Section title="Contact & Identity" />
          <FormLayout.Grid>
            <Input
              // Required for a head only — it is the family contact (validateMember).
              label="Mobile Number" required={showFamilyFields} maxLength={12} placeholder="7 to 12 digits"
              value={formData.mobileNumber} onChange={setField('mobileNumber')}
              error={errors.mobileNumber}
            />
            <Input
              label="Aadhaar Number" maxLength={12} placeholder="12 digits"
              value={formData.aadhaarNumber} onChange={setField('aadhaarNumber')}
              error={errors.aadhaarNumber}
            />
            <Input
              label="Area / Locality" placeholder="e.g. Anna Nagar"
              value={formData.location} onChange={setField('location')}
            />
          </FormLayout.Grid>

          {/* 4. Their place in the church. */}
          <FormLayout.Section title="Church Membership" />
          <FormLayout.Grid>
            <SearchableSelect
              label={`Member Status (${GUEST_STATUS} = temporary stay)`} placeholder="Search or type a status..."
              value={formData.memberStatus}
              onChange={(v) => setFormData((prev) => {
                const next = { ...prev, memberStatus: v };
                // Turning someone into a guest starts the stay today, for the
                // parish's default period, unless dates were already entered.
                if (isGuest(next) && !prev.guestFrom) {
                  const from = todayISO();
                  next.guestFrom = from;
                  if (!prev.guestUntil) next.guestUntil = addMonths(from, guestMonths);
                }
                return next;
              })}
              options={memberStatuses.options}
              onOptionsChange={memberStatuses.setOptions}
            />
            {/* Optional — type/scan a number, or leave blank and one is generated
                automatically on save, like the Family ID. */}
            <Input
              label="Membership No." placeholder="Leave blank to auto-generate"
              hint="Optional — filled in automatically if left empty."
              value={formData.barcodeMembershipNo} onChange={setField('barcodeMembershipNo')}
              error={errors.barcodeMembershipNo}
            />
            {isEditing ? (
              <Input
                label="Life Status" value={lifeLabel(formData.status)} disabled
                hint="To change it, use Change status in the member's menu."
              />
            ) : (
              <Select
                label="Life Status" placeholder=""
                options={LIFE_OPTIONS}
                value={formData.status}
                onChange={setField('status')}
                hint="Usually Active. Drives the Active / Inactive / Transferred / Passed Away tabs."
              />
            )}
            {formData.status === 'Deceased' && (
              <Input
                label="Date of Death" type="date"
                value={formData.dateOfDeath} onChange={setField('dateOfDeath')}
                error={errors.dateOfDeath}
              />
            )}
            {formData.status === 'Transferred' && (
              <Input
                label="Transferred To (Church)" required placeholder="Name of receiving church"
                value={formData.transferChurch} onChange={setField('transferChurch')}
                error={errors.transferChurch}
              />
            )}
          </FormLayout.Grid>

          {/* A guest's stay: when it began, when it is expected to end, where
              they came from and why. Shown only while the status is Guest. */}
          {isGuest(formData) && (
            <>
              <FormLayout.Section title="Guest Stay" />
              <FormLayout.Grid>
                <Input
                  label="Guest since" type="date" required
                  value={formData.guestFrom}
                  onChange={(e) => {
                    const from = e.target.value;
                    setErrors((prev) => (prev.guestFrom ? { ...prev, guestFrom: undefined } : prev));
                    // Keep the default period unless the end was set by hand.
                    setFormData((prev) => ({
                      ...prev, guestFrom: from,
                      guestUntil: prev.guestUntil && prev.guestFrom && prev.guestUntil !== addMonths(prev.guestFrom, guestMonths)
                        ? prev.guestUntil
                        : (from ? addMonths(from, guestMonths) : prev.guestUntil)
                    }));
                  }}
                  error={errors.guestFrom}
                />
                <Input
                  label="Expected until" type="date"
                  hint={`Leave blank for an open-ended stay. Default is ${guestMonths} months (Settings › Rules).`}
                  value={formData.guestUntil} onChange={setField('guestUntil')}
                  error={errors.guestUntil}
                />
                <Input
                  label="Home church / came from" placeholder="e.g. CSI Church, Nagercoil"
                  value={formData.guestOrigin} onChange={setField('guestOrigin')}
                />
                <Input
                  label="Purpose of stay" placeholder="e.g. College, work posting, staying with family"
                  value={formData.guestPurpose} onChange={setField('guestPurpose')}
                />
              </FormLayout.Grid>
            </>
          )}

          {/* 5. Sacraments. */}
          <FormLayout.Section title="Sacraments" />
          <FormLayout.Grid>
            <Input
              label="Baptism Date" type="date"
              value={formData.baptismDate} onChange={setField('baptismDate')}
              error={errors.baptismDate}
            />
            <Input
              label="Confirmation Date" type="date"
              value={formData.confirmationDate} onChange={setField('confirmationDate')}
              error={errors.confirmationDate}
            />
            <Select
              label="Communion Status"
              options={withStoredValue(COMMUNION_STATUSES, formData.communionStatus)}
              value={formData.communionStatus} onChange={setField('communionStatus')}
            />
          </FormLayout.Grid>

          {/* 6. Certificate scans only. Hidden entirely when the parish turns
              uploads off (Settings › Rules & Formats). */}
          {documentsEnabled && (
            <>
              <FormLayout.Section title="Documents" />
              <FormLayout.Grid>
                {DOCUMENTS.map((doc) => (
                  <FileInput
                    key={doc.key}
                    label={doc.label}
                    fileName={formData[doc.key]}
                    onFile={(name, dataUrl) =>
                      setFormData((prev) => ({ ...prev, [doc.key]: name, [`${doc.key}Url`]: dataUrl }))}
                    onError={(msg) => notify(msg, 'error')}
                  />
                ))}
              </FormLayout.Grid>
            </>
          )}
        </FormLayout>
      )}

      {viewingMember && (
        <Drawer
          title={viewingMember.name}
          subtitle={viewingMember.memberId}
          onClose={() => setViewingMember(null)}
          footer={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* Active, Inactive, Transferred, Passed Away — instead of deleting. */}
              {!isVoided(viewingMember) && (
                <Button
                  write variant="secondary" fullWidth style={{ padding: '12px', fontSize: '16px' }}
                  onClick={() => openChangeStatus(viewingMember)}
                >
                  🔄 Change status
                </Button>
              )}

              {/* A living guest: extend, take onto the roll, or mark as gone home. */}
              {isGuest(viewingMember) && lifeOf(viewingMember) === 'Alive' && (
                <>
                  <Button
                    fullWidth style={{ padding: '12px', fontSize: '16px' }}
                    onClick={() => setPendingConvert(viewingMember)}
                  >
                    ⭐ Make Permanent Member
                  </Button>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Button variant="secondary" fullWidth onClick={() => requestExtend(viewingMember)}>
                      📅 Extend Stay
                    </Button>
                    <Button variant="secondary" fullWidth onClick={() => setPendingLeave(viewingMember)}>
                      🚪 Guest Left
                    </Button>
                  </div>
                </>
              )}

              {/* Anyone active who is not already the head can be promoted. */}
              {!['deceased', 'transferred'].includes(statusOf(viewingMember))
                && !isHead(viewingMember.relationToHead) && (
                <Button
                  variant="secondary" fullWidth style={{ padding: '12px', fontSize: '16px' }}
                  onClick={() => setPendingPromote(viewingMember)}
                >
                  👑 Promote to Head of Family
                </Button>
              )}

              <PrintButton
                module="Members & Families"
                documentType="MEMBER_RECORD"
                data={() => ({
                  ...viewingMember,
                  headOfFamily: headNameFor(viewingMember.familyId),
                  dateOfBirth: formatDate(viewingMember.dateOfBirth),
                  dateOfMarriage: formatDate(viewingMember.dateOfMarriage),
                  membershipDate: formatDate(viewingMember.membershipDate),
                  baptismDate: formatDate(viewingMember.baptismDate),
                  confirmationDate: formatDate(viewingMember.confirmationDate)
                })}
                title={`Member-${viewingMember.memberId || viewingMember.id}`}
                onNotify={notify}
                isDarkMode={isDarkMode}
              />

              <Button variant="ghost" fullWidth write style={{ padding: '12px', fontSize: '16px' }}
                onClick={() => startEdit(viewingMember)}>
                Edit Member
              </Button>
            </div>
          }
        >
          {/* Photo hidden from the UI on request. Kept here, commented out,
              so it can be restored.

          {viewingMember.photoDataUrl && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                  Full photo, no crop. Click to view it full size.
              <div
                onClick={() => setPreviewDoc({
                  label: 'Member Photo', fileName: viewingMember.photo || 'photo', dataUrl: viewingMember.photoDataUrl
                })}
                title="Click to view"
                style={{
                  width: '130px', height: '130px', borderRadius: '12px',
                  border: `1px solid ${t.border}`, backgroundColor: t.bgSubtle, cursor: 'pointer',
                  overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <img
                  src={viewingMember.photoDataUrl}
                  alt={viewingMember.name}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
                />
              </div>
            </div>
          )}
          */}

          <div style={{ textAlign: 'center', marginBottom: '10px' }}>
            <Badge tone={lifeOf(viewingMember) === 'Deceased' ? 'neutral' : lifeOf(viewingMember) === 'Alive' ? 'success' : 'warning'}>
              {lifeLabel(viewingMember.status)}
            </Badge>
          </div>

          <Drawer.Section title="Family" />
          <Drawer.Row label="Family ID" value={viewingMember.familyId} />
          <Drawer.Row label="Head of Family" value={headNameFor(viewingMember.familyId) || '-'} />
          <Drawer.Row label="Relation to Head" value={viewingMember.relationToHead} />
          <Drawer.Row label="Member Status" value={viewingMember.memberStatus} />
          {viewingMember.statusDate && <Drawer.Row label="Status since" value={formatDate(viewingMember.statusDate)} />}
          {viewingMember.statusReason && <Drawer.Row label="Status note" value={viewingMember.statusReason} />}
          {viewingMember.membershipDate && (
            <Drawer.Row label="Member Since" value={formatDate(viewingMember.membershipDate)} />
          )}

          {/* The stay while they are a guest; kept as history once on the roll. */}
          {(isGuest(viewingMember) || viewingMember.guestFrom) && (
            <>
              <Drawer.Section title={isGuest(viewingMember) ? 'Guest Stay' : 'Former Guest Stay'} />
              <Drawer.Row label="Since" value={formatDate(viewingMember.guestFrom)} />
              <Drawer.Row label="Until" value={formatDate(viewingMember.guestUntil)} />
              {isGuest(viewingMember) && statusOf(viewingMember) === 'alive' && (
                <Drawer.Row label="Stay">
                  <Badge tone={GUEST_TONE[guestPhase(viewingMember)]}>
                    {guestPhaseLabel(guestPhase(viewingMember), daysUntil(viewingMember.guestUntil))}
                  </Badge>
                </Drawer.Row>
              )}
              <Drawer.Row label="Home Church / From" value={viewingMember.guestOrigin} />
              <Drawer.Row label="Purpose" value={viewingMember.guestPurpose} />
            </>
          )}

          <Drawer.Section title="Personal" />
          <Drawer.Row label="S/o, D/o, W/o" value={viewingMember.relativeName} />
          <Drawer.Row label="Gender" value={viewingMember.gender} />
          <Drawer.Row label="Location" value={viewingMember.location} />
          <Drawer.Row label="Date of Birth" value={formatDate(viewingMember.dateOfBirth)} />
          <Drawer.Row label="Marital Status" value={viewingMember.maritalStatus} />
          {hasMarriageDate(viewingMember.maritalStatus) && (
            <Drawer.Row label="Date of Marriage" value={formatDate(viewingMember.dateOfMarriage)} />
          )}
          <Drawer.Row label="Blood Group" value={viewingMember.bloodGroup} />
          <Drawer.Row label="Education" value={viewingMember.education} />
          <Drawer.Row label="Occupation" value={viewingMember.occupation} />
          <Drawer.Row label="Mobile" value={viewingMember.mobileNumber} />
          {/* The number itself is never shown here — only whether one is on file. */}
          <Drawer.Row
            label="Aadhaar"
            value={viewingMember.aadhaarNumber ? '[Aadhaar Redacted]' : '-'}
          />

          <Drawer.Section title="Sacraments" />
          <Drawer.Row label="Baptism Date" value={formatDate(viewingMember.baptismDate)} />
          <Drawer.Row label="Confirmation Date" value={formatDate(viewingMember.confirmationDate)} />
          <Drawer.Row label="Communion" value={viewingMember.communionStatus} />
          {viewingMember.createdBy && <Drawer.Row label="Created By" value={viewingMember.createdBy} />}

          {statusOf(viewingMember) === 'deceased' && (
            <>
              <Drawer.Section title="Demise" />
              <Drawer.Row label="Date of Death" value={formatDate(viewingMember.dateOfDeath)} />
            </>
          )}
          {statusOf(viewingMember) === 'transferred' && (
            <>
              <Drawer.Section title="Transfer" />
              <Drawer.Row label="Transferred To" value={viewingMember.transferChurch} />
            </>
          )}

          {documentsEnabled && (
            <>
              <Drawer.Section title="Documents" />
              {DOCUMENTS.map((doc) => (
                <Drawer.Row key={doc.key} label={doc.label}>
                  {viewingMember[`${doc.key}Url`] ? (
                    <button
                      type="button"
                      onClick={() => setPreviewDoc({
                        label: doc.label,
                        fileName: viewingMember[doc.key],
                        dataUrl: viewingMember[`${doc.key}Url`]
                      })}
                      style={{
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        color: t.primaryText, fontWeight: 'bold', textDecoration: 'underline',
                        fontSize: 'inherit', textAlign: 'left'
                      }}
                    >
                      {viewingMember[doc.key] || 'Open'}
                    </button>
                  ) : '-'}
                </Drawer.Row>
              ))}
            </>
          )}
        </Drawer>
      )}

      {/* --- In-app certificate preview --- */}
      {previewDoc && (
        <Modal
          open
          width="800px"
          title={previewDoc.label}
          onClose={() => setPreviewDoc(null)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPreviewDoc(null)}>Close</Button>
            </>
          }
        >
          <div style={{ textAlign: 'center' }}>
            {isPdf(previewDoc) ? (
              <iframe
                title={previewDoc.label}
                src={previewDoc.dataUrl}
                style={{ width: '100%', height: '65vh', border: `1px solid ${t.border}` }}
              />
            ) : (
              <img
                src={previewDoc.dataUrl}
                alt={previewDoc.label}
                style={{ maxWidth: '100%', maxHeight: '65vh', objectFit: 'contain' }}
              />
            )}
            <div style={{ marginTop: '10px', color: t.textMuted, fontSize: t.fontSize.sm }}>
              {previewDoc.fileName}
            </div>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        open={Boolean(dupWarnings)}
        title="Possible duplicate"
        message={
          <>
            {(dupWarnings || []).map((w) => <span key={w.field + w.message} style={{ display: 'block', marginBottom: '6px' }}>{w.message}</span>)}
            Save this member anyway?
          </>
        }
        confirmLabel="Save anyway"
        cancelLabel="Go back"
        destructive={false}
        onConfirm={() => { setDupWarnings(null); handleSubmit(undefined, true); }}
        onCancel={() => setDupWarnings(null)}
      />

      <ConfirmDialog
        open={Boolean(pendingPromote)}
        title="Promote to Head of Family"
        message={
          `Make ${pendingPromote?.name || ''} the head of family ${pendingPromote?.familyId || ''}? `
          + 'The current head will be changed to "Other" and the family renamed.'
        }
        confirmLabel="Promote"
        destructive={false}
        onConfirm={() => promoteToHead(pendingPromote)}
        onCancel={() => setPendingPromote(null)}
      />

      <ConfirmDialog
        open={Boolean(pendingConvert)}
        title="Make Permanent Member"
        message={
          `Take ${pendingConvert?.name || ''} onto the roll as a ${ROLL_STATUS} member from today? `
          + 'The guest stay dates are kept in the record as history.'
        }
        confirmLabel="Make member"
        destructive={false}
        onConfirm={() => convertGuest(pendingConvert)}
        onCancel={() => setPendingConvert(null)}
      />

      <ConfirmDialog
        open={Boolean(pendingLeave)}
        title="Guest Left"
        message={
          `Mark ${pendingLeave?.name || ''}'s stay as ended? `
          + `They move to the Transferred tab${pendingLeave?.guestOrigin ? ` (to ${pendingLeave.guestOrigin})` : ''} `
          + 'and can be restored later if they return.'
        }
        confirmLabel="Mark as left"
        destructive={false}
        onConfirm={() => guestLeft(pendingLeave)}
        onCancel={() => setPendingLeave(null)}
      />

      {/* Extend a stay: quick jumps from the current end date, or any date. */}
      <Modal
        open={Boolean(pendingExtend)}
        title={`📅 Extend Stay — ${pendingExtend?.name || ''}`}
        onClose={() => setPendingExtend(null)}
        width="440px"
        footer={
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setPendingExtend(null)}>Cancel</Button>
            <Button onClick={extendGuest} disabled={saving}>{saving ? 'Saving…' : 'Save new date'}</Button>
          </div>
        }
      >
        <p style={{ margin: '0 0 12px', color: t.textMuted, fontSize: t.fontSize.md }}>
          Currently until <strong style={{ color: t.textMain }}>{formatDate(pendingExtend?.guestUntil, 'no end date')}</strong>.
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          {[[3, '+3 months'], [6, '+6 months'], [12, '+1 year']].map(([months, label]) => (
            <Button
              key={label}
              variant="secondary" size="sm"
              onClick={() => {
                // From the later of today and the current end, so an expired
                // stay is extended from now rather than from the past.
                const base = [pendingExtend?.guestUntil || '', todayISO()].sort().pop() as string;
                setExtendUntil(addMonths(base, Number(months)));
              }}
            >
              {label}
            </Button>
          ))}
        </div>
        <Input
          label="New end date" type="date" fullWidth
          value={extendUntil} onChange={(e) => setExtendUntil(e.target.value)}
        />
      </Modal>

      {statusChange && (
        <ChangeStatusDialog
          member={statusChange.member}
          family={statusChange.family}
          familyRecord={families.find((f) => familyIdOf(f) === statusChange.member.familyId) || null}
          admin={admin}
          isDarkMode={isDarkMode}
          loadFull={loadForWrite}
          onCancel={() => setStatusChange(null)}
          onError={(msg) => notify(msg, 'error')}
          onDone={async (msg) => {
            setStatusChange(null);
            setViewingMember(null);
            await refetch();
            notify(msg, 'success');
          }}
        />
      )}

      {/* Only for a record made by mistake; the reason is mandatory. */}
      <PromptDialog
        open={Boolean(pendingDelete)}
        title="Remove wrong entry"
        message={
          `Remove ${pendingDelete?.name || ''} (${pendingDelete?.memberId || ''}) as a wrong entry — for example typed twice? `
          + 'Only for mistakes: someone who left or passed away gets a new status instead (Change status). '
          + 'The record goes to Deleted History and can be brought back. Enter the reason:'
        }
        placeholder="e.g. Duplicate of MEM-8003"
        confirmLabel="Remove"
        destructive
        onSubmit={handleVoid}
        onCancel={() => setPendingDelete(null)}
      />

      {/* Add Head sent the new family for approval (Settings › Approvals). */}
      <ConfirmDialog
        open={Boolean(sentFamily)}
        destructive={false}
        title="📝 Sent for approval"
        confirmLabel={onOpenPending ? 'Open Pending Approval' : 'OK'}
        cancelLabel="Done"
        message={sentFamily && (
          <span style={{ display: 'block', lineHeight: 1.9 }}>
            <span style={{ display: 'block' }}>
              {sentFamily.headName}&apos;s family is waiting for approval under&nbsp;
              <strong style={{ fontFamily: 'monospace' }}>{sentFamily.referenceNo}</strong>.
            </span>
            {sentFamily.waitingFor.length > 0 && (
              <span style={{ display: 'block' }}>Next to approve: {sentFamily.waitingFor.join(' or ')}</span>
            )}
            <span style={{ display: 'block' }}>
              Once approved, the family and its head are added with their Family ID and Membership No.;
              then add the rest of the household.
            </span>
          </span>
        )}
        onConfirm={() => { setSentFamily(null); onOpenPending?.(); }}
        onCancel={() => setSentFamily(null)}
      />

    </div>
  );
};

export default MemberManagement;
